import { type FC, useContext, useState } from 'react';
import {
  useCompleteContractMutation,
  useRejectContractMutation,
} from '@api/queries/contract';
import { useCompletionContractCheck } from '@common/hooks/completion-contract-check';
import {
  type ContractData,
  type ContractDataType,
} from '@common/types/contract';
import { useAppDispatch, useAppSelector } from '@redux/store';
import { logEvent } from 'firebase/analytics';

import { useCheckVIPstatus } from '@/common/hooks/checkVIPstatus';
import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { checkIfContractHasProductOfDay } from '@/common/lib/contract/check-if-contract-has-product-of-day';
import { playSound } from '@/common/lib/soundEffect';
import { CompleteContractAnimationContext } from '@/components/Animations/CompleteContractAnimation/context';
import { analytics } from '@/firebase';
import { removeContractById } from '@/redux/reducers/contractReducer';
import { addToBalance, changeStorage } from '@/redux/reducers/userReducer';

import { ModalContext } from '../Modal/config';

import Default from './Default/Default';
import GoldenAndCustom from './GoldenAndCustom/GoldenAndCustom';

interface TakeContractProps {
  contractData?: ContractDataType;
  seconds?: number;
  nextGoldContractSeconds?: number;
  tutorial?: 'Custom' | 'Golden';
}

const TakeContract: FC<TakeContractProps> = ({
  contractData,
  seconds,
  nextGoldContractSeconds,
  tutorial,
}) => {
  const contractDataFallback: ContractDataType = {
    id: '',
    data: { wood: { amount: 0 }, iron: { amount: 0 } } as ContractData,
    cash: 0,
    point: 0,
    dice: 0,
    createdAt: null,
    rejectedAt: null,
    type: 'Default',
    timeLeft: 0,
    isOpened: undefined,
    cost: undefined,
  };
  const { closeModal, setOverlayIsActive, openModal } =
    useContext(ModalContext);
  const {
    setIsAnimationStart,
    isAnimationStart,
    setReward,
    setContractId,
    contractListWrapperRef,
    lastCompleteContractHasProductOfDayRef,
  } = useContext(CompleteContractAnimationContext);
  const contract =
    useAppSelector((state) =>
      state.contract.list?.find((el) => el.id === contractData?.id),
    ) ??
    contractData ??
    contractDataFallback;
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const hasContractCompleted = useCompletionContractCheck(contract.data);
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const [completeContract, completeContractApiState] =
    useCompleteContractMutation({ fixedCacheKey: 'shared-complete-contract' });
  const [rejectContract, rejectContractApiState] = useRejectContractMutation();
  const nextGoldContract = useCountDownTimer(nextGoldContractSeconds || 0);
  const statusVIP = useCheckVIPstatus();
  const isFirstVisit = useAppSelector((state) => state.user.isFirstVisit);
  const productsOfDay = useAppSelector((state) => state.contract.productOfDay);

  const foundProductOfDay =
    productsOfDay?.filter((product) =>
      Object.keys(contract?.data).includes(product.name),
    ) ?? [];

  const extraReward = foundProductOfDay.reduce(
    (acc, product) => acc + product.extraStarPoints,
    0,
  );

  const isPrimaryContracts: boolean =
    ['Golden', 'GoldenPlus', 'Custom'].includes(contract.type) || !!tutorial;

  const onComplete = (): void => {
    if (hasContractCompleted) {
      playSound('buttons/switch');

      if (!isFirstVisit) {
        setOverlayIsActive(true);
        setReward({
          cash:
            statusVIP && !isPrimaryContracts
              ? contract.cash * 1.1
              : contract.cash,
          point: contract.point + (extraReward || 0),
          dice: contract.dice || 0,
          ...contract.lotteryTickets,
          bingoBalls: contract.bingoBalls || 0,
        });
        setContractId(contract.id);
      }
      const contractHasProductOfDay = checkIfContractHasProductOfDay(
        productsOfDay,
        contract.data,
      );

      completeContract({
        contractId: contract.id,
      })
        .unwrap()
        .then((data) => {
          if (contractListWrapperRef.current) {
            contractListWrapperRef.current.scroll({
              left: 0,
              top: 0,
              behavior: 'smooth',
            });
          }
          const storageData: Record<string, number> = {};
          for (const [key, value] of Object.entries(contract.data)) {
            storageData[key] = value.amount;
          }
          lastCompleteContractHasProductOfDayRef.current =
            contractHasProductOfDay;
          dispatch(changeStorage(storageData));
          dispatch(
            addToBalance({
              cash:
                statusVIP && !isPrimaryContracts
                  ? contract.cash * 1.1
                  : contract.cash,
              bingoBalls: contract.bingoBalls || 0,
            }),
          );

          dispatch(removeContractById(contract.id));

          if (!isFirstVisit) setIsAnimationStart(true);
          closeModal();

          const productOfDay = data.productOfDay.find(
            (product) =>
              Object.keys(contract.data).includes(product.name) &&
              product.progress >= product.countToReward &&
              !product.chestBoxOpenedAt,
          );

          if (productOfDay) {
            openModal({
              renderKind: 'productOfDayModal',
              props: { productId: productOfDay.id },
            });
          }
        });

      if (!analytics) return;
      Object.entries(contract.data).forEach(([name, value]) => {
        if (!analytics) return;
        logEvent(analytics, 'currency_spent', {
          currency_type: name,
          amount: value.amount,
          reason: 'contract_completion',
          contract_id: contract.id,
        });
      });
      logEvent(analytics, 'currency_earned', {
        currency_type: 'cash',
        amount: contract.cash,
        source: 'contract_completion',
        contract_id: contract.id,
      });
      logEvent(analytics, 'currency_earned', {
        currency_type: 'contract_point',
        amount: contract.point,
        source: 'contract_completion',
        contract_id: contract.id,
      });
    }
  };

  const onReject = (): void => {
    playSound('buttons/deny');
    rejectContract({ contractId: contract.id })
      .unwrap()
      .then(() => {
        setIsCancelConfirmOpen(false);
        closeModal();
      });

    if (!analytics) return;
    logEvent(analytics, 'contract_cancel', {
      reward_cash: contract.cash,
      reward_point: contract.point,
      user_level: user?.level,
      resources_to_complete: JSON.stringify(contract.data),
    });
  };

  const withOneItem = Object.keys(contract.data).length === 1;

  const goldenProps = {
    closeModal,
    seconds,
    nextGoldContract,
    contractData: tutorial ? { ...contract, type: tutorial } : contract,
    isAnimationStart,
    completeContractApiState,
    rejectContractApiState,
    hasContractCompleted,
    onComplete,
    onReject,
    statusVIP,
    cost: contract.cost,
    tutorial,
  };
  const defaultProps = {
    closeModal,
    contractData: contract,
    isAnimationStart,
    completeContractApiState,
    rejectContractApiState,
    hasContractCompleted,
    onComplete,
    withOneItem,
    setIsCancelConfirmOpen,
    isCancelConfirmOpen,
    user,
    onReject,
    statusVIP,
  };

  return (
    <>
      {isPrimaryContracts ? (
        <GoldenAndCustom {...goldenProps} />
      ) : (
        <Default {...defaultProps} />
      )}
    </>
  );
};

export { TakeContract };
